/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useEligibilityData
 */
import _ from 'lodash';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { JsonRule } from '@react-awesome-query-builder/antd';
import api from 'client/index';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { addRuleToQuery, prepareQueryRule } from 'utils/queryBuilder';
import { getStatus } from 'utils/eligibility/eligibilityResponse';
import { dataKeys } from 'utils/eligibility/eligibilityRequests';
import QUERY_KEYS from 'app-constants/queryKeys';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { ELIGIBILITY_ADF_FIELD_MAP } from 'utils/adfUtils';

/**
 * use eligibility data hook
 * @returns {UseQueryResult} useEligibilityData
 */

interface IProps {
	apiParams: EligibilityRequests.IApiParams;
	pageNumber: number;
	pageSize: number;
	coverageId?: string;
	options?: IQueryOptions;
}

// function to get coverage rule
const getCoverageRule = (coverageId: string): JsonRule => {
	return prepareQueryRule({
		field: dataKeys.coverageId,
		operator: OPERATORS.equal,
		value: [coverageId],
	});
};
const useAllEligibilityRequests = ({
	apiParams,
	pageNumber,
	pageSize,
	coverageId,
	options,
}: IProps): UseQueryResult<{ count: number; data: EligibilityRequests.IEligibilityData[] }> => {
	return useQuery({
		queryKey: [QUERY_KEYS.eligibilityRequests, apiParams, pageNumber, pageSize, coverageId],
		queryFn: async () => {
			let criteria = parseQueryFilterParams({
				query: apiParams?.criteria,
				field: dataKeys.coverageStatus,
				parser: getStatus,
			});
			if (coverageId) {
				const coverageRule = getCoverageRule(coverageId);
				criteria = addRuleToQuery(criteria, coverageRule);
			}
			const { data } = await api.EzapiApi.getEligibilityRequests({
				eligibilityRequestQueryModel: {
					...apiParams,
					...(buildCriteria(_.cloneDeep(criteria), ELIGIBILITY_ADF_FIELD_MAP) || {}),
				},
				pgNum: pageNumber,
				pgSize: pageSize,
			});
			return data;
		},
		...options,
	});
};

export { getCoverageRule };
export default useAllEligibilityRequests;
